import { useMemo, type JSX } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Section } from '@/components/blocks/layout';
import { SectionHeader } from '@/components/blocks/page';
import { NavigableEntityCard, Panel } from '@/components/blocks/data-display';
import { EmptyState } from '@/components/blocks/feedback';
import type { RefrigerationUnitInput } from '@/data/repos/refrigerationUnitsRepo';
import type { RefrigerationUnit } from '@/data/schemas';
import { catalogService } from '@/data/services/catalogService';
import { queryKeys } from '@/lib/query-keys';
import { CATALOG_IDS } from '@/data/models/catalog';
import { resolveFieldValueLabel } from '@/features/catalogs/utils/resolveFieldValue';
import { useCreateRefrigerationUnit } from '../../hooks';
import { useDialogState } from '@/hooks/useDialogState';
import { RefrigerationUnitModal } from './RefrigerationUnitModal';

const NUMBER_FORMATTER = new Intl.NumberFormat('it-IT', {
  maximumFractionDigits: 1,
});

interface RefrigerationUnitsOverviewProps {
  companyId: string;
  siteId: string;
  refrigerationUnits: RefrigerationUnit[];
  contextLine?: string;
}

function formatMetric(value: number | null | undefined, unit: string): string {
  if (value === undefined || value === null) return '—';
  return `${NUMBER_FORMATTER.format(value)} ${unit}`;
}

export function RefrigerationUnitsOverview({
  companyId,
  siteId,
  refrigerationUnits,
  contextLine,
}: RefrigerationUnitsOverviewProps): JSX.Element {
  const navigate = useNavigate({
    from: '/companies/$companyId/sites/$siteId',
  });
  const createRefrigerationUnit = useCreateRefrigerationUnit(companyId, siteId);
  const [dialogOpen, setDialogOpen] = useDialogState('siteDetailNewUnit');
  const { data: plantLocationOptions = [] } = useQuery({
    queryKey: queryKeys.catalogs.options(CATALOG_IDS.PLANT_LOCATIONS),
    queryFn: () =>
      catalogService.getCatalogOptions(CATALOG_IDS.PLANT_LOCATIONS, {
        includeInactive: true,
      }),
  });
  const { data: plantTypeOptions = [] } = useQuery({
    queryKey: queryKeys.catalogs.options(CATALOG_IDS.PLANT_TYPES),
    queryFn: () =>
      catalogService.getCatalogOptions(CATALOG_IDS.PLANT_TYPES, {
        includeInactive: true,
      }),
  });

  const sortedRefrigerationUnits = useMemo(() => {
    const toComparableId = (unit: RefrigerationUnit) => {
      const identifier = unit.refrigerationUnitId?.trim();
      if (identifier) return identifier;

      const name = unit.name?.trim();
      if (name) return name;

      return unit.id.trim();
    };

    return [...refrigerationUnits].sort((a, b) => {
      const idA = toComparableId(a);
      const idB = toComparableId(b);
      if (!idA && !idB) return 0;
      if (!idA) return 1;
      if (!idB) return -1;
      return idA.localeCompare(idB, 'it', { numeric: true });
    });
  }, [refrigerationUnits]);

  const handleOpenDetail = (unitId: string) => {
    navigate({
      to: '/companies/$companyId/sites/$siteId/units/$unitId',
      params: { companyId, siteId, unitId },
      search: { tab: undefined, safety: undefined },
    }).catch(() => {
      /* navigation errors handled globally */
    });
  };

  const handleCreate = async (input: RefrigerationUnitInput): Promise<string> => {
    return createRefrigerationUnit.mutateAsync(input);
  };

  const handleCreated = (unitId: string) => {
    navigate({
      to: '/companies/$companyId/sites/$siteId/units/$unitId',
      params: { companyId, siteId, unitId },
      search: { tab: undefined, safety: undefined },
    }).catch(() => {
      /* navigation errors handled globally */
    });
  };

  return (
    <Section spacing="lg">
      <SectionHeader
        title="Impianti"
        action={{
          label: 'Nuovo impianto',
          onClick: () => setDialogOpen(true),
        }}
      />

      {sortedRefrigerationUnits.length === 0 ? (
        <Panel>
          <EmptyState
            icon="device_thermostat"
            title="Nessun impianto censito"
            description="Aggiungi almeno un impianto frigorifero per abilitare la generazione dei report e monitorare i sistemi di sicurezza."
            variant="inline"
          />
        </Panel>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {sortedRefrigerationUnits.map((refrigerationUnit) => {
            const components = refrigerationUnit.technical?.components ?? [];
            const componentQuantity = components.reduce(
              (total, component) => total + (component.quantity ?? 0),
              0
            );
            return (
              <NavigableEntityCard
                key={refrigerationUnit.id}
                title={refrigerationUnit.name}
                icon="ac_unit"
                rows={[
                  {
                    label: 'Ubicazione:',
                    value:
                      resolveFieldValueLabel(refrigerationUnit.location, plantLocationOptions) ||
                      '—',
                  },
                  {
                    label: 'Tipo impianto:',
                    value:
                      resolveFieldValueLabel(refrigerationUnit.impiantoType, plantTypeOptions) ||
                      '—',
                  },
                  {
                    label: 'Carica NH₃:',
                    value: formatMetric(refrigerationUnit.ammoniaKg, 'kg'),
                  },
                  {
                    label: 'Componenti:',
                    value: `${components.length} / ${componentQuantity}`,
                  },
                ]}
                onClick={() => handleOpenDetail(refrigerationUnit.id)}
              />
            );
          })}
        </div>
      )}

      <RefrigerationUnitModal
        open={dialogOpen}
        mode="create"
        onOpenChange={setDialogOpen}
        contextLine={contextLine}
        onCreate={handleCreate}
        onCreated={handleCreated}
        onDelete={undefined}
        isSubmitting={createRefrigerationUnit.isPending}
      />
    </Section>
  );
}
